import { useEffect, useState } from "react";
import { MdDownload } from "react-icons/md";
import { BrandButton } from "../components/ui/BrandButton";
import { useAppLoading } from "../context/AppLoadingContext";
import { getAdminTnuaQrs } from "../services/tnuaQrService";
import { buildTnuaQrDataUrl, generateTnuaQrPdf } from "../utils/tnuaQrPdf";

function TnuaQrCard({ tnua }) {
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const buildQr = async () => {
      setError("");
      try {
        const dataUrl = await buildTnuaQrDataUrl(tnua.qrToken);
        if (!isMounted) {
          return;
        }

        setQrDataUrl(dataUrl);
      } catch {
        if (isMounted) {
          setError("No pudimos generar el QR.");
        }
      }
    };

    void buildQr();

    return () => {
      isMounted = false;
    };
  }, [tnua.qrToken]);

  const handleDownload = async () => {
    setIsGenerating(true);
    setError("");

    try {
      const dataUrl = qrDataUrl || (await buildTnuaQrDataUrl(tnua.qrToken));
      await generateTnuaQrPdf({ tnua, qrDataUrl: dataUrl });
    } catch {
      setError("No pudimos preparar el PDF.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <article className="grid gap-4 rounded-[2rem] border border-brand-secondary/15 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="grid gap-1">
          <h3 className="text-lg font-semibold text-brand-primary">{tnua.name}</h3>
        </div>
      </div>

      <div className="grid place-items-center rounded-[1.75rem] border border-brand-secondary/10 bg-brand-surface p-4">
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt={`QR de ${tnua.name}`}
            className="h-60 w-60 rounded-xl bg-white p-3 shadow-[0_10px_24px_rgba(20,50,135,0.10)]"
          />
        ) : (
          <div className="flex h-60 w-60 items-center justify-center rounded-xl bg-white text-sm text-slate-500">
            Generando QR...
          </div>
        )}
      </div>

      <div className="grid gap-3">
        <div className="rounded-2xl border border-brand-secondary/10 bg-brand-surface px-4 py-3 text-xs font-mono text-slate-700 break-all">
          {tnua.qrToken}
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        ) : null}

        <BrandButton
          type="button"
          onClick={handleDownload}
          variant="secondary"
          radius="full"
          disabled={isGenerating}
        >
          <MdDownload aria-hidden="true" className="h-5 w-5" />
          {isGenerating ? "Generando PDF..." : "Descargar PDF"}
        </BrandButton>
      </div>
    </article>
  );
}

export function AdminQrPage() {
  const { trackAsync } = useAppLoading();
  const [tnuas, setTnuas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadQrCodes = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await getAdminTnuaQrs();
        if (!isMounted) {
          return;
        }

        setTnuas(response.tnuas ?? []);
      } catch (requestError) {
        if (!isMounted) {
          return;
        }

        setError(requestError.message || "No pudimos cargar los QR de tnua.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void trackAsync(loadQrCodes);

    return () => {
      isMounted = false;
    };
  }, [trackAsync]);

  return (
    <div className="grid gap-5">
      <section className="px-1 py-1 sm:px-2 sm:py-2">
        <h2 className="font-display text-3xl text-brand-primary sm:text-4xl">
          QR por tnua
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
          Desde acá podés ver el QR estable de cada tnua y descargarlo en PDF para imprimirlo o compartirlo.
        </p>
      </section>

      {error ? (
        <div className="rounded-[2rem] border border-rose-200 bg-rose-50 px-6 py-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="rounded-[2rem] border border-brand-secondary/15 bg-white px-6 py-8 text-sm text-slate-600">
          Cargando QR de tnua...
        </div>
      ) : null}

      {!isLoading && !error ? (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {tnuas.map((tnua) => (
            <TnuaQrCard key={tnua.id} tnua={tnua} />
          ))}
        </section>
      ) : null}
    </div>
  );
}
